import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Divider,
  Checkbox,
  Avatar,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import MainLayout from "../../components/MainLayout";
import TotalCounter from "../../components/TotalCounter";
import MakeAccountCard from "../../components/MakeAccountCard";
import { SummaryDataGrid } from "../../components/AppDataGrid";
import ActorDetails from "../../components/Actor/ActorDetails";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteArtistProfiles,
  fetchArtistDataById,
  getArtistList,
  setArtist,
} from "../../actions/artist";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Verified,
} from "@mui/icons-material";
import { StorageConstants } from "../../storage/storageConstants";
import {
  deepCopy,
  hideLoadingOverlay,
  showLoadingOverlay,
} from "../../../utils";
import { useMessage } from "../../../base/MessageProvider";
import ActorProfileForm from "../../components/Actor/ActorProfileForm";
import SlideStack from "../../components/SlideStack";
import { getGridStringOperators } from "@mui/x-data-grid";
import { getProfileCategoryList } from "../../actions/profileCategory";
import {
  getArtistExperienceCategoryList,
  getLocationList,
  getRecruiterExperienceCategoryList,
} from "../../actions/general";
import OAuth from "../../components/OAuth";
import { getUserKycList } from "../../actions/kyc";
import PicUploadModal from "../../../recruiter/pages/Auth/components/PicUploadModal";
import KycConfirmationModal from "../../components/KycConfirmationModal";

/**
 * @author Sarthak Sharma.
 * @since 27-12-2023.
 * @description Actor management page.
 * @returns React.ReactElement
 */

const KYCManagement = () => {
  const storedPage = localStorage.getItem(StorageConstants.KYC_PAGE);
  const storedPageSize = localStorage.getItem(StorageConstants.KYC_PER_PAGE);

  const min900px = useMediaQuery("(min-width: 900px)");

  const dispatch = useDispatch();
  const alert = useMessage();
  const loading = useSelector((state) => state.artistReducer?.loading);
  const userKycList =
    useSelector((state) => state.kycReducer?.userKycList) ?? [];
  const userKycListMetaData = useSelector(
    (state) => state.kycReducer?.userKycListMetaData
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [checkedId, setCheckedId] = useState([]);
  const [modalOpen, setModalOpen] = useState({
    open: false,
    index: -1,
  });
  const [rowsState, setRowsState] = useState({
    page: storedPage ? +storedPage : 0,
    pageSize: storedPageSize ? +storedPageSize : 50,
  });

  const totalCounts = +userKycListMetaData?.totalPaginatedUser || 0;

  useEffect(() => {
    const init = async () => {
      dispatch(getArtistExperienceCategoryList({ categoryType: "Artist" }));
      dispatch(
        getRecruiterExperienceCategoryList({ categoryType: "Recruiter" })
      );
    };
    init();
  }, []);

  const onPaginationModelChange = (model) => {
    setRowsState(model);
    localStorage.setItem(StorageConstants.KYC_PAGE, JSON.stringify(model.page));
    localStorage.setItem(
      StorageConstants.KYC_PER_PAGE,
      JSON.stringify(model.pageSize)
    );
  };

  const onFilterModelChange = (model) => {
    const filterModel = deepCopy(model);
    console.log(filterModel.items);

    if (filterModel.items.length && !filterModel.items[0].value) {
      setSearchTerm("");
      return;
    }
    setSearchTerm(filterModel.items[0]?.value);
  };

  const onRowClick = async (params) => {
    console.log("Print params row >>> ", params.id);
    setModalOpen({ open: true, index: params.id });
  };

  const handleNextClick = () => {
    const rowsLength = rows?.length;
    const { index } = modalOpen;
    if (index + 1 >= rowsLength) {
      if (rowsState.page >= Math.ceil(totalCounts / rowsState.pageSize) - 1)
        return;
      onPaginationModelChange({
        ...rowsState,
        page: rowsState.page + 1,
      });
      setModalOpen({ ...modalOpen, index: 0 });
    } else {
      setModalOpen({ ...modalOpen, index: index + 1 });
    }
  };

  const handlePrevClick = () => {
    const { index } = modalOpen;
    const rowsLength = rows?.length;
    if (index - 1 < 0) {
      if (rowsState.page <= 0) return;
      onPaginationModelChange({
        ...rowsState,
        page: rowsState.page - 1,
      });
      setModalOpen({ ...modalOpen, index: rowsLength - 1 });
    } else {
      setModalOpen({ ...modalOpen, index: index - 1 });
    }
  };

  const fetchData = async () => {
    setRows([]);
    dispatch(
      getUserKycList({
        page: +rowsState.page + 1,
        perPage: rowsState.pageSize,
        searchedKeyword: searchTerm,
      })
    );
  };

  useEffect(() => {
    if (userKycList) {
      setRows(
        userKycList.map((item, index) => ({
          ...item,
          id: index,
          code: `#${item.userId?.substr(item.userId?.length - 5)}`,
          fullName: `${item.firstName} ${item?.lastName ?? ""}`,
          profilePic: item?.userProfile?.profilePic?.url,
          profileCategory:
            item?.userProfile?.profileCategories
              ?.map?.((item) => item.name)
              ?.join?.(", ") ?? "",
        }))
      );
    }
  }, [userKycList]);

  useEffect(() => {
    fetchData();
  }, [rowsState, searchTerm]);

  const columns = useMemo(
    () => [
      {
        flex: 1,
        field: "fullName",
        headerName: "Name",
        description: "This column has a value getter and is not sortable.",
        minWidth: 200,
        renderCell: (params) => (
          <div className="flex gap-3 items-center text-[12px] font-['Poppins-Regular'] text-[#202223]">
            <OAuth
              // altSrc={params.row.profilePic}
              component={
                <Avatar
                  sx={{ width: 30, height: 30 }}
                  src={params.row.profilePic}
                />
              }
            />
            <div className="flex gap-1 items-center">
              {params.value}
              {params.row?.verified && (
                <Verified className="text-[#5D43FF] text-base" />
              )}
            </div>
          </div>
        ),
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        field: "code",
        headerName: "Code",
        valueGetter: (params) => params.value ?? "-",
        width: 90,
        filterable: false,
      },
      {
        flex: 1,
        field: "email",
        headerName: "Email",
        valueGetter: (params) => params.value ?? "-",
        minWidth: 188,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        // flex: 1,
        field: "mobileNo",
        headerName: "Mobile No.",
        valueGetter: (params) => params.value ?? "-",
        width: 130,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      {
        flex: 1,
        field: "profileCategory",
        headerName: "Category",
        valueGetter: (params) => params.value ?? "-",
        renderCell: (params) => {
          return (
            <Tooltip
              className="text-[11px] text-black font-medium"
              title={params?.value ?? "-"}
            >
              {params?.value ?? "-"}
            </Tooltip>
          );
        },
        minWidth: 130,
        filterOperators: getGridStringOperators().filter(
          (a) => a.value === "contains"
        ),
      },
      // {
      //     // flex: 1,
      //     field: 'isOnline',
      //     headerName: 'Status',
      //     valueGetter: (params) =>
      //         params.value ? 'Online' : 'Offline',
      //     renderCell: (params) =>
      //         <div className="flex gap-2 items-center text-[12px] font-['Poppins-Regular'] text-[#202223]">
      //             {params.row?.isOnline ? <div className="w-[10px] h-[10px] rounded-full bg-[#488C60]" /> : <div className="w-[10px] h-[10px] rounded-full bg-[#D9D9D9]" />}
      //             {params.value}
      //         </div>,
      //     width: 92,
      // },
      //   {
      //     field: "userId",
      //     headerName: "",
      //     type: "actions",
      //     renderCell: (params) => (
      //       <Checkbox
      //         checked={checkedId?.includes(params.value)}
      //         onChange={handleChange?.bind(this, params.value)}
      //         icon={<RadioButtonUnchecked />}
      //         color="secondary"
      //         checkedIcon={<CheckCircle />}
      //       />
      //     ),
      //     width: 60,
      //     sortable: false,
      //     filter: false,
      //   },
    ],
    [checkedId]
  );

  const onSuccess = (isSuccessStatus) => {
    if (isSuccessStatus === "DENIED") {
      fetchData();
    } else if (isSuccessStatus === "APPROVED") {
      fetchData();
    }
  };

  return (
    <MainLayout>
      <Box className="relative w-full flex flex-col gap-8 items-center p-4 py-12">
        <Box className="w-full flex bg-white rounded-[12px] border-[2px] border-[#E8E8E9] ">
          <SummaryDataGrid
            title="KYC Verification List"
            subTitle="Approve or Reject the profiles at your wish."
            paginationModel={rowsState}
            onPaginationModelChange={onPaginationModelChange}
            onFilterModelChange={onFilterModelChange}
            rowCount={totalCounts}
            columns={columns}
            rows={rows}
            loading={loading}
            onRowClick={onRowClick}
            isDeleteAllBtn={false}
            isPagination={false}
            // onPressDelete={onPressDeleteAll}
          />
          {/* <DevDataGrid /> */}
        </Box>
      </Box>
      {modalOpen.open && (
        <KycConfirmationModal
          open={modalOpen.open}
          onClose={setModalOpen.bind(this, {
            open: false,
            data: null,
            index: 0,
          })}
          item={rows?.[modalOpen?.index]}
          onSuccess={onSuccess.bind(this)}
          prevDisabled={modalOpen?.index - 1 < 0 && rowsState.page <= 0}
          nextDisabled={
            modalOpen?.index + 1 >= rows.length &&
            rowsState.page >= Math.ceil(totalCounts / rowsState.pageSize) - 1
          }
          onNext={handleNextClick}
          onPrevious={handlePrevClick}
        />
      )}
    </MainLayout>
  );
};

export default React.memo(KYCManagement, () => true);
